import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import { TrendingUp, Lock, Mail, User, ArrowLeft, KeyRound, CheckCircle2, Smartphone, Home, BarChart3, BookOpen, DollarSign, Coffee } from "lucide-react";

/**
 * domínio REAL (online)
 */
const CANONICAL_ORIGIN = "https://cofy.app";

function joinUrl(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${b}/${p}`;
}

function isIp(hostname) {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(String(hostname || ""));
}

function looksLikePhone(value) {
  const v = String(value || "").trim();
  if (v === "") return false;

  // Se houver @ ou letras, o usuário está digitando e-mail.
  if (v.includes("@") || /[a-zA-Z]/.test(v)) return false;

  // Qualquer entrada numérica (mesmo ainda incompleta) é celular.
  return /\d/.test(v);
}

function formatPhone(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function normalizeIdentifierInput(value) {
  return looksLikePhone(value) ? formatPhone(value) : value;
}

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(true);

    const existing = document.querySelector('script[data-gsi="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", reject);
      return;
    }

    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.dataset.gsi = "1";
    s.onload = () => resolve(true);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function LoginPage({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({ email: "", password: "", name: "" });
  const [referralCode, setReferralCode] = useState("");

  // Recuperação de senha
  const [authView, setAuthView] = useState("auth"); // auth | forgot | reset | reset_done
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [googleClientId, setGoogleClientId] = useState("");
  const [googleBase, setGoogleBase] = useState("");
  const [googleReady, setGoogleReady] = useState(false);
  const [googleRenderTick, setGoogleRenderTick] = useState(0);

  const gsiContainerRef = useRef(null);

  // ✅ Se estiver acessando via IP local, força usar o domínio online
  const ORIGIN = useMemo(() => {
    const host = window.location.hostname;
    return isIp(host) ? CANONICAL_ORIGIN : window.location.origin;
  }, []);

  // ✅ bases candidatas (com e sem rewrite)
  const API_BASES = useMemo(() => {
    const root = `${ORIGIN}/api`;
    return [`${root}/trader`, `${root}/trader/index.php`];
  }, [ORIGIN]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = String(params.get("token") || "").trim();
    const ref = String(params.get("ref") || "").trim().toUpperCase();

    if (ref) {
      setReferralCode(ref);
      localStorage.setItem("cofy_referral_code", ref);
    } else {
      const savedRef = String(localStorage.getItem("cofy_referral_code") || "").trim().toUpperCase();
      if (savedRef) setReferralCode(savedRef);
    }

    if (token) {
      setResetToken(token);
      setAuthView("reset");
      setIsLogin(true);
    }
  }, []);

  // ✅ aplica token salvo
  useEffect(() => {
    const t = localStorage.getItem("access_token");
    if (t) axios.defaults.headers.common.Authorization = `Bearer ${t}`;
  }, []);

  async function tryLoginOnBase(base) {
    const formBody = new URLSearchParams();
    formBody.append("username", formData.email.trim());
    formBody.append("password", formData.password);

    const url = joinUrl(base, "/auth/login");
    return axios.post(url, formBody, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      timeout: 12000,
    });
  }

  async function tryRegisterOnBase(base) {
    const url = joinUrl(base, "/auth/register");
    return axios.post(
      url,
      { name: formData.name, identifier: formData.email.trim(), password: formData.password, referral_code: referralCode || undefined },
      { timeout: 12000 }
    );
  }

  async function tryForgotPasswordOnBase(base, email) {
    const url = joinUrl(base, "/auth/forgot-password");
    return axios.post(
      url,
      { email: String(email || "").trim() },
      { timeout: 12000 }
    );
  }

  async function tryResetPasswordOnBase(base, token, password) {
    const url = joinUrl(base, "/auth/reset-password");
    return axios.post(
      url,
      { token, password },
      { timeout: 12000 }
    );
  }

  async function tryGetGoogleClientId(base) {
    const url = joinUrl(base, "/auth/google/client-id");
    return axios.get(url, { timeout: 8000 });
  }

  async function tryGoogleOnBase(base, credential) {
    const url = joinUrl(base, "/auth/google");
    return axios.post(url, { id_token: credential }, { timeout: 12000 });
  }

  function applyAuthSuccess(user, token, usedBase) {
    localStorage.setItem("access_token", token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("api_base_used", usedBase || "");
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    onLogin(user, token);
  }

  // ✅ busca google_client_id do admin (via API), carrega script e renderiza botão
  useEffect(() => {
    let cancelled = false;

    async function bootGoogle() {
      try {
        let foundId = "";
        let foundBase = "";

        for (const base of API_BASES) {
          try {
            const r = await tryGetGoogleClientId(base);
            const cid = String(r?.data?.client_id || "").trim();
            if (cid) {
              foundId = cid;
              foundBase = base;
              break;
            }
          } catch (_) {
            // tenta próxima base
          }
        }

        if (cancelled) return;

        if (!foundId) {
          setGoogleClientId("");
          setGoogleBase("");
          setGoogleReady(false);
          return; // admin ainda não configurou
        }

        setGoogleClientId(foundId);
        setGoogleBase(foundBase);

        await loadGoogleScript();
        if (cancelled) return;

        // inicializa
        window.google.accounts.id.initialize({
          client_id: foundId,
          callback: async (resp) => {
            try {
              const credential = resp?.credential;
              if (!credential) {
                toast.error("Google não retornou credential.");
                return;
              }

              // usa a base que achou o client_id (garante rota)
              const r = await tryGoogleOnBase(foundBase, credential);
              const data = r?.data ?? {};
              const user = data.user ?? data.profile ?? null;
              const token = data.access_token ?? data.token ?? data.jwt ?? null;

              if (!user || !token) {
                console.error("[GoogleLogin] sem user/token:", data);
                toast.error("Servidor não retornou user/token no Google login.");
                return;
              }

              toast.success("Login Google OK!");
              applyAuthSuccess(user, token, foundBase);
            } catch (err) {
              const status = err?.response?.status;
              const data = err?.response?.data;
              console.error("[GoogleLogin] erro:", { status, data, err });

              toast.error(
                data?.detail ||
                  (status === 401 ? "Token Google inválido (401)." : "Falha no login Google.")
              );
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Primeiro monta o container do botão no React.
        // O renderButton será chamado no useEffect abaixo, depois que o ref existir.
        setGoogleReady(true);
        setGoogleRenderTick((v) => v + 1);
      } catch (e) {
        console.error("[GoogleBoot] falhou:", e);
        setGoogleReady(false);
      }
    }

    bootGoogle();
    return () => {
      cancelled = true;
    };
  }, [API_BASES]);

  // ✅ Renderiza o botão somente DEPOIS que googleReady montar o container/ref.
  // Antes, o código tentava renderizar enquanto googleReady ainda era false,
  // então gsiContainerRef.current normalmente era null.
  useEffect(() => {
    if (!googleReady || !googleClientId) return;
    if (!window.google?.accounts?.id) return;
    if (!gsiContainerRef.current) return;

    try {
      gsiContainerRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(gsiContainerRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        width: 320,
      });
    } catch (e) {
      console.error("[GoogleButton] falhou ao renderizar:", e);
      toast.error("Não foi possível carregar o botão do Google.");
    }
  }, [googleReady, googleClientId, googleRenderTick]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    const email = recoveryEmail.trim();
    if (!email) {
      toast.error("Informe seu e-mail.");
      return;
    }

    setLoading(true);

    try {
      let response = null;
      let lastErr = null;

      for (const base of API_BASES) {
        try {
          response = await tryForgotPasswordOnBase(base, email);
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          if (status === 400 || status === 429) break;
        }
      }

      if (!response) {
        const msg =
          lastErr?.response?.data?.detail ||
          lastErr?.response?.data?.message ||
          "Não foi possível solicitar a recuperação de senha.";
        toast.error(msg);
        return;
      }

      toast.success(
        response?.data?.message ||
          "Se existir uma conta com esse e-mail, enviaremos as instruções."
      );
      setAuthView("auth");
      setIsLogin(true);
      setFormData((prev) => ({ ...prev, email }));
    } catch (error) {
      console.error("[ForgotPassword] erro:", error);
      toast.error("Erro inesperado ao solicitar recuperação.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!resetToken) {
      toast.error("Link de recuperação inválido.");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("A nova senha deve ter pelo menos 8 caracteres.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("As senhas não coincidem.");
      return;
    }

    setLoading(true);

    try {
      let response = null;
      let lastErr = null;

      for (const base of API_BASES) {
        try {
          response = await tryResetPasswordOnBase(base, resetToken, newPassword);
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          if (status === 400 || status === 401) break;
        }
      }

      if (!response) {
        const msg =
          lastErr?.response?.data?.detail ||
          lastErr?.response?.data?.message ||
          "Não foi possível redefinir a senha.";
        toast.error(msg);
        return;
      }

      localStorage.removeItem("access_token");
      delete axios.defaults.headers.common.Authorization;

      toast.success(response?.data?.message || "Senha alterada com sucesso.");
      setNewPassword("");
      setConfirmNewPassword("");
      setResetToken("");
      setAuthView("reset_done");

      // Remove o token da barra de endereço sem recarregar a página.
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error("[ResetPassword] erro:", error);
      toast.error("Erro inesperado ao redefinir a senha.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      let lastErr = null;
      let response = null;
      let usedBase = null;

      for (const base of API_BASES) {
        try {
          response = isLogin ? await tryLoginOnBase(base) : await tryRegisterOnBase(base);
          usedBase = base;
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          if (status === 400 || status === 401) break;
        }
      }

      if (!response) {
        const status = lastErr?.response?.status;
        const data = lastErr?.response?.data;
        console.error("[LoginPage] Falha auth:", { status, data, lastErr });

        const msg =
          data?.detail ||
          data?.message ||
          (status === 401
            ? "E-mail/celular ou senha inválidos (401)."
            : status === 404
            ? "Rota não encontrada (404). Falta rewrite (.htaccess) ou base errada."
            : "Erro ao autenticar (ver console/network).");

        toast.error(msg);
        return;
      }

      const data = response?.data ?? {};
      const user = data.user ?? data.profile ?? null;
      const token = data.access_token ?? data.token ?? data.jwt ?? null;

      if (!user || !token) {
        console.error("[LoginPage] Resposta sem user/token:", data);
        toast.error("Servidor não retornou user/token. Veja console.");
        return;
      }

      toast.success(isLogin ? "Login OK!" : "Conta criada com sucesso!");

      if (!isLogin && referralCode) {
        localStorage.removeItem("cofy_referral_code");
        setReferralCode("");
      }

      applyAuthSuccess(user, token, usedBase);
    } catch (error) {
      console.error("[LoginPage] Erro geral:", error);
      toast.error("Erro inesperado ao autenticar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8 sm:px-6"
      style={{
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%)",
      }}
    >
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />

      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-10 items-center relative z-10">
        {/* Left side • vitrine COFY */}
        <div className="text-white space-y-6 hidden lg:block">
          <button
            type="button"
            onClick={() => { window.location.href = "/"; }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-slate-200 transition hover:border-cyan-400/30 hover:bg-cyan-400/10 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para COFY
          </button>

          <div className="flex items-center gap-3 pt-2">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-300/20 bg-gradient-to-br from-blue-600 to-cyan-500 shadow-lg shadow-blue-950/30">
              <Coffee className="h-7 w-7 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-300">COFY • TRADER</p>
              <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                Mercado profissional do café
              </h1>
            </div>
          </div>

          <div>
            <h2 className="max-w-xl text-5xl font-black leading-[1.05] tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
              Informação para acompanhar o mercado.
              <span className="mt-2 block bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">
                Tecnologia para decidir.
              </span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300" style={{ fontFamily: "Inter, sans-serif" }}>
              Acompanhe Bolsa de Nova York, dólar, livro de ordens e ferramentas de inteligência da COFY em uma única plataforma.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 backdrop-blur">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300">
                <BarChart3 className="h-5 w-5" />
              </div>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Mercado</p>
              <strong className="mt-1 block text-sm text-white">Bolsa de NY</strong>
              <span className="mt-1 block text-[10px] text-slate-400">Café arábica</span>
            </div>

            <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 backdrop-blur">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300">
                <DollarSign className="h-5 w-5" />
              </div>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Câmbio</p>
              <strong className="mt-1 block text-sm text-white">Dólar</strong>
              <span className="mt-1 block text-[10px] text-slate-400">Referência BRL</span>
            </div>

            <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 backdrop-blur">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-300">
                <BookOpen className="h-5 w-5" />
              </div>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">COFY</p>
              <strong className="mt-1 block text-sm text-white">Livro de ordens</strong>
              <span className="mt-1 block text-[10px] text-slate-400">Visão do mercado</span>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-2xl border border-cyan-400/15 bg-cyan-400/5 px-4 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-400/10">
              <TrendingUp className="h-4 w-4 text-cyan-300" />
            </div>
            <div>
              <p className="text-xs font-bold text-white">Cotações e inteligência COFY</p>
              <p className="mt-0.5 text-[10px] text-slate-400">Entre na sua conta para acessar todos os recursos do Trader.</p>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="lg:hidden mb-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => { window.location.href = "/"; }}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/10 bg-slate-900/70 px-3 text-xs font-bold text-slate-200"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para COFY
            </button>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500">
                <Coffee className="h-5 w-5 text-white" />
              </div>
              <div className="text-right">
                <p className="text-[8px] font-black uppercase tracking-[0.18em] text-cyan-300">COFY</p>
                <p className="text-[10px] font-bold text-white">Mercado do café</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl border border-white/10 bg-slate-950/35 px-2 py-2 text-center">
              <BarChart3 className="mx-auto h-4 w-4 text-emerald-300" />
              <p className="mt-1 text-[9px] font-bold text-white">Bolsa NY</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-950/35 px-2 py-2 text-center">
              <DollarSign className="mx-auto h-4 w-4 text-cyan-300" />
              <p className="mt-1 text-[9px] font-bold text-white">Dólar</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-950/35 px-2 py-2 text-center">
              <BookOpen className="mx-auto h-4 w-4 text-blue-300" />
              <p className="mt-1 text-[9px] font-bold text-white">Ordens</p>
            </div>
          </div>
        </div>

        <Card className="w-full max-w-xl mx-auto border-white/10 bg-slate-900/60 backdrop-blur-xl shadow-2xl rounded-3xl overflow-hidden" data-testid="auth-card">
          <CardHeader className="px-5 pt-7 pb-4 sm:px-8 sm:pt-8">
            <CardTitle className="text-3xl sm:text-4xl text-white leading-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
              {authView === "forgot"
                ? "Recuperar senha"
                : authView === "reset"
                ? "Criar nova senha"
                : authView === "reset_done"
                ? "Senha alterada"
                : "Bem-vindo"}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base text-slate-400 mt-2">
              {authView === "forgot"
                ? "Informe seu e-mail para receber o link de recuperação"
                : authView === "reset"
                ? "Escolha uma nova senha para sua conta"
                : authView === "reset_done"
                ? "Sua senha foi atualizada com segurança"
                : "Entre ou crie sua conta para começar"}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-5 pb-7 sm:px-8 sm:pb-8">
            {authView === "forgot" ? (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div className="space-y-2.5">
                  <Label htmlFor="recovery-email" className="text-sm sm:text-base font-medium text-slate-200">
                    <Mail className="w-4 h-4 inline mr-2" /> Email
                  </Label>
                  <Input
                    id="recovery-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    required
                    className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 sm:h-16 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-base sm:text-lg text-white font-bold shadow-lg shadow-emerald-950/20 transition-all hover:-translate-y-0.5 active:translate-y-0"
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "Enviar link de recuperação"}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setAuthView("auth");
                    setIsLogin(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar para o login
                </button>

                <p className="text-[11px] leading-relaxed text-slate-500 text-center">
                  Por segurança, a mensagem exibida será a mesma mesmo que o e-mail não esteja cadastrado.
                </p>
              </form>
            ) : authView === "reset" ? (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-3 text-xs text-slate-300">
                  <KeyRound className="w-4 h-4 inline mr-2 text-emerald-300" />
                  Digite uma nova senha com pelo menos 8 caracteres.
                </div>

                <div className="space-y-2.5">
                  <Label htmlFor="new-password" className="text-sm sm:text-base font-medium text-slate-200">
                    <Lock className="w-4 h-4 inline mr-2" /> Nova senha
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                  />
                </div>

                <div className="space-y-2.5">
                  <Label htmlFor="confirm-new-password" className="text-sm sm:text-base font-medium text-slate-200">
                    <Lock className="w-4 h-4 inline mr-2" /> Confirmar nova senha
                  </Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 sm:h-16 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-base sm:text-lg text-white font-bold shadow-lg shadow-emerald-950/20 transition-all hover:-translate-y-0.5 active:translate-y-0"
                  disabled={loading}
                >
                  {loading ? "Alterando..." : "Salvar nova senha"}
                </Button>
              </form>
            ) : authView === "reset_done" ? (
              <div className="space-y-5 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <CheckCircle2 className="h-7 w-7 text-emerald-300" />
                </div>

                <div>
                  <p className="text-sm font-semibold text-white">Senha alterada com sucesso</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Faça login novamente usando sua nova senha.
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    setAuthView("auth");
                    setIsLogin(true);
                  }}
                  className="w-full h-14 sm:h-16 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-base sm:text-lg text-white font-bold shadow-lg shadow-emerald-950/20 transition-all hover:-translate-y-0.5 active:translate-y-0"
                >
                  Ir para o login
                </Button>
              </div>
            ) : (
              <>
                <Tabs defaultValue="login" className="w-full" onValueChange={(v) => setIsLogin(v === "login")}>
                  <TabsList className="grid w-full h-14 grid-cols-2 mb-7 rounded-xl bg-slate-800/90 p-1">
                    <TabsTrigger value="login" data-testid="login-tab" className="h-12 rounded-lg text-sm sm:text-base font-semibold data-[state=active]:bg-slate-700 data-[state=active]:text-white">Login</TabsTrigger>
                    <TabsTrigger value="register" data-testid="register-tab" className="h-12 rounded-lg text-sm sm:text-base font-semibold data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white">Criar conta</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2.5">
                        <Label htmlFor="email" className="text-sm sm:text-base font-medium text-slate-200">
                          <Mail className="w-4 h-4 inline mr-2" /> Email
                        </Label>
                        <Input
                          id="email"
                          data-testid="login-email-input"
                          type="text" 
                          inputMode={looksLikePhone(formData.email) ? "tel" : "text"}
                           autoComplete="username"
                           placeholder="Digite seu E-MAIL ou CELULAR com DDD"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: normalizeIdentifierInput(e.target.value) })}
                          required
                          className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                        />
                      </div>

                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between gap-3">
                          <Label htmlFor="password" className="text-sm sm:text-base font-medium text-slate-200">
                            <Lock className="w-4 h-4 inline mr-2" /> Senha
                          </Label>

                          <button
                            type="button"
                            onClick={() => {
                              setRecoveryEmail(looksLikePhone(formData.email) ? "" : formData.email || "");
                              setAuthView("forgot");
                            }}
                            className="text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition"
                          >
                            Esqueci minha senha
                          </button>
                        </div>

                        <Input
                          id="password"
                          data-testid="login-password-input"
                          type="password"
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-14 sm:h-16 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-base sm:text-lg text-white font-bold shadow-lg shadow-emerald-950/20 transition-all hover:-translate-y-0.5 active:translate-y-0"
                        disabled={loading}
                        data-testid="login-submit-button"
                      >
                        {loading ? "Entrando..." : "Entrar"}
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="register">
                    <div className="mb-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                      <p className="text-sm font-semibold text-emerald-300">Crie sua conta gratuita</p>
                      <p className="mt-1 text-xs sm:text-sm text-slate-400">
                        Leva menos de um minuto. Preencha seus dados para acessar o COFY Trader.
                      </p>
                    </div>
                    {referralCode && (
                      <div className="mb-5 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-cyan-300">
                          Convite de parceiro COFY
                        </p>
                        <p className="mt-1 text-sm text-slate-300">
                          Seu cadastro será vinculado automaticamente ao parceiro deste convite.
                        </p>
                      </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2.5">
                        <Label htmlFor="name" className="text-sm sm:text-base font-medium text-slate-200">
                          <User className="w-4 h-4 inline mr-2" /> Nome
                        </Label>
                        <Input
                          id="name"
                          data-testid="register-name-input"
                          type="text"
                          placeholder="Seu nome"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                        />
                      </div>

                      <div className="space-y-2.5">
                        <Label htmlFor="reg-email" className="text-sm sm:text-base font-medium text-slate-200">
                          <Mail className="w-4 h-4 inline mr-2" /> Email
                        </Label>
                        <Input
                          id="reg-email"
                          data-testid="register-email-input"
                          type="text" 
                          inputMode={looksLikePhone(formData.email) ? "tel" : "text"}
                           autoComplete="username"
                           placeholder="Digite seu E-MAIL ou CELULAR com DDD"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: normalizeIdentifierInput(e.target.value) })}
                          required
                          className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                        />
                      </div>

                      <div className="space-y-2.5">
                        <Label htmlFor="reg-password" className="text-sm sm:text-base font-medium text-slate-200">
                          <Lock className="w-4 h-4 inline mr-2" /> Senha
                        </Label>
                        <Input
                          id="reg-password"
                          data-testid="register-password-input"
                          type="password"
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          className="h-14 sm:h-16 rounded-xl bg-slate-800/90 border-slate-600 text-xl sm:text-2xl font-semibold text-white placeholder:text-lg sm:placeholder:text-xl placeholder:font-semibold placeholder:text-slate-300 px-4 focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-14 sm:h-16 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-base sm:text-lg text-white font-bold shadow-lg shadow-emerald-950/20 transition-all hover:-translate-y-0.5 active:translate-y-0"
                        disabled={loading}
                        data-testid="register-submit-button"
                      >
                        {loading ? "Criando..." : "Criar Conta"}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>

                <div className="relative my-7">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-700"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-900 px-2 text-slate-400">Ou continue com</span>
                  </div>
                </div>

                {/* ✅ Botão Google real */}
                {googleReady ? (
                  <div className="w-full flex justify-center">
                    <div ref={gsiContainerRef} />
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-14 sm:h-16 rounded-xl border-slate-600 bg-slate-800 text-base sm:text-lg font-semibold text-white hover:bg-slate-700"
                    onClick={() => toast.info("Configure o Google Client ID no admin do Trader para ativar.")}
                    data-testid="google-login-button"
                  >
                    Google
                  </Button>
                )}

                {googleClientId ? (
                  <p className="mt-3 text-xs text-slate-500 break-all">
                    
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-slate-500">
                    Google desativado (client_id não configurado no admin).
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}